import type { FleetV2Response } from '@emma/api_client/types';
import { useToast } from '@lego/klik-ui';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import type { DetailSectionProps } from '@/components/common/details/DetailsBox';
import { EditableDetailsBox } from '@/components/common/details/EditableDetailsBox';
import { useUpdateFleet } from '@/hooks/fleet';
import { useCanWriteById } from '@/hooks/workspace';
import { ToastFactory } from '@/utility/toast_factory';

const FIELD_NAME_MAP: Record<string, keyof FleetV2Response> = {
  Description: 'description',
  Name: 'name',
};

const NAME_PATTERN = /^[a-z0-9._]{5,100}$/;

const validateFleetName = (name: string): string | null => {
  if (!NAME_PATTERN.test(name)) {
    return 'Name must be 5-100 characters and contain only lowercase letters, numbers, dots, or underscores';
  }
  return null;
};

type Props = {
  fleet: FleetV2Response;
  sections: DetailSectionProps[];
  additionalComponent?: ReactElement;
};

export const EditableFleetDetails = ({ fleet, sections, additionalComponent }: Props) => {
  const [editedValue, setEditedValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const canWrite = useCanWriteById(fleet.workspaceId);

  const { mutate } = useSWRConfig();
  const updateFleet = useUpdateFleet(fleet.workspaceId, fleet.id);
  const toast = useToast();

  const cacheKey = `workspaces/${fleet.workspaceId}/fleets/${fleet.id}`;

  const handleStartEdit = useCallback((key: string, currentValue: ReactNode) => {
    setEditedValue(String(currentValue ?? ''));
    setEditingKey(key);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditedValue('');
    setEditingKey(null);
  }, []);

  const handleSave = useCallback(
    async (key: string, value?: string) => {
      const valueToSave = value ?? editedValue;
      const apiFieldName = FIELD_NAME_MAP[key];

      if (!apiFieldName) return;

      if (apiFieldName === 'name') {
        const validationError = validateFleetName(valueToSave);
        if (validationError) {
          toast(ToastFactory.validationError(validationError));
          return;
        }
      }

      setIsSaving(true);

      try {
        await updateFleet({ [apiFieldName]: valueToSave });
        await mutate(cacheKey);
        setEditingKey(null);
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      } finally {
        setIsSaving(false);
      }
    },
    [editedValue, mutate, updateFleet, cacheKey, toast]
  );

  return (
    <EditableDetailsBox
      additionalComponent={additionalComponent}
      editableFields={canWrite ? ['Description', 'Name'] : []}
      editedValue={editedValue}
      editingKey={editingKey}
      isSaving={isSaving}
      onCancelEdit={handleCancelEdit}
      onSave={handleSave}
      onStartEdit={handleStartEdit}
      onValueChange={setEditedValue}
      sections={sections}
      title={fleet.name}
    />
  );
};
